import { type Hsl, type Rgb, rgbToHsl } from '@/util/color'
import { getOrSet, logError, type Unsubscribe } from '@amazon/vinyl-util'
import { circularDifference, idealCenteredScore } from '@/util/math'
import { maxBy } from '@/util/array'
import type { ImageModel } from '@/components/ImageView'
import { onImageDecode } from '@/util/imageElement'
import { setBgColors } from '@/components/BackgroundColors'
import type { MaybeObservableValue, ObservableValue } from '@amazon/vinyl-observable'
import { asData, externalData } from '@amazon/vinyl-observable'
import { ctx } from '@/util/context'

export type ThreeColorSwatch = [Hsl | null, Hsl | null, Hsl | null]

const S = 10 // Take S ** 2 samples

export function getTriadicColor(img: HTMLImageElement): ThreeColorSwatch | null {
    const canvas = document.createElement('canvas')
    canvas.width = S
    canvas.height = S

    const ctx = canvas.getContext('2d', {
        willReadFrequently: false,
        colorSpace: 'display-p3',
    })
    if (!ctx) return null
    ctx.imageSmoothingEnabled = false // skip expensive bilinear scaling

    ctx.drawImage(img, 0, 0, S, S)

    const { data } = ctx.getImageData(0, 0, S, S)

    // Sampling blocks across the image in a grid
    type Sample = { n: number } & Hsl
    const colorCount = new Map<number, Sample>()

    let highestN = 0
    const rgb: Rgb = { r: 0, g: 0, b: 0, a: 1 }
    const hsl: Hsl = { h: 0, s: 0, l: 0, a: 1 }
    for (let y = 0; y < S; y++) {
        for (let x = 0; x < S; x++) {
            // Get center of the block
            const offset = (y * S + x) * 4

            rgb.r = data[offset]
            rgb.g = data[offset + 1]
            rgb.b = data[offset + 2]

            rgbToHsl(rgb, hsl)

            // Bin saturation and lightness in 10 segment chunks
            const hBin = ((hsl.h / 360) * 15) | 0
            const sBin = (hsl.s * 5) | 0
            const lBin = (hsl.l * 5) | 0
            const key = hBin * 10000 + sBin * 100 + lBin

            const sample: Sample = getOrSet(colorCount, key, () => ({
                h: 0,
                s: 0,
                l: 0,
                a: 1,
                n: 0,
            }))
            sample.n++
            sample.h = hsl.h
            sample.s = hsl.s
            sample.l = hsl.l
            if (sample.n > highestN) {
                highestN = sample.n
            }
        }
    }

    // For debugging:
    // console.log(
    //     'colorCount:',
    //     Array.from(colorCount.values()).sort(
    //         compareBy((v) => -scoreSample([], v))
    //     )
    // )

    function softTriadicScore(hueDiff: number): number {
        const d = Math.min(Math.abs(hueDiff - 120), Math.abs(hueDiff - 240))
        return Math.max(0, 1 - d / 60) // Full score at 120/240, fades to 0 at ±60°
    }

    function scoreSample(exclude: readonly (Hsl | null)[], sample: Sample): number {
        const closenessPenalty =
            0.05 +
            0.95 *
                exclude.reduce((acc, c) => {
                    if (!c) return acc
                    const hueDiff = circularDifference(sample.h, c.h, 360)
                    return acc * softTriadicScore(hueDiff)
                }, 1)

        const lightnessScore = idealCenteredScore(sample.l, 0.4)
        const saturationScore = idealCenteredScore(sample.s, 0.9)
        const relevanceScore = sample.n / highestN
        return (relevanceScore + 2 * lightnessScore + 2 * saturationScore) * closenessPenalty
    }

    function pickColor(exclude: readonly (Hsl | null)[]): Hsl | null {
        return (
            maxBy(Array.from(colorCount.values()), (sample) => scoreSample(exclude, sample)) ?? null
        )
    }

    const c1 = pickColor([])
    if (!c1) return null
    const c2 = pickColor([c1])
    const c3 = pickColor([c1, c2])
    return [c1, c2, c3]
}

const { target } = ctx('triadicColor')

export function triadicColorData(
    imageModel: MaybeObservableValue<ImageModel>
): ObservableValue<ThreeColorSwatch | null> {
    const img = new Image()
    let disposed = false

    return externalData<ThreeColorSwatch | null>(
        null,
        (setData: (value: ThreeColorSwatch | null) => void): Unsubscribe => {
            const dataSub = asData(imageModel).onData((value) => {
                setData(null)
                img.crossOrigin = 'anonymous'
                const sub = onImageDecode(img, () => {
                    if (disposed) return
                    try {
                        setData(getTriadicColor(img))
                    } catch (error) {
                        logError(target, 'error setting bg colors', error)
                    }
                })
                if (value.src) img.src = value.src
                if (value.srcset) img.srcset = value.srcset
                return sub
            })

            return () => {
                disposed = true
                dataSub()
                setBgColors(null)
            }
        }
    )
}
